import { useRef, useEffect, forwardRef, useImperativeHandle } from "react";

import dayjs from "dayjs";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";

import { useForm } from "@mantine/form";
import { DatePicker } from "@mantine/dates";
import {
  Flex,
  Button,
  Select,
  TextInput,
  SimpleGrid,
  createStyles,
} from "@mantine/core";

import { PhoneNumberInput } from "@components/PhoneNumberInput/PhoneNumberInput";

import { Draft } from "@utils/Draft.util";
import { useLanguageCode } from "@hooks/useLanguageCode";
import { currentStepAtom } from "../atoms/currentStep.atom";
import { getDefaultPhoneCodeUtil } from "@utils/getDefaultPhoneCode.util";

import { IIVolunteeringFormData } from "../VolunteeringFormRoute.types";
import { IStepRef } from "@models/Step.model";
import { IUser } from "@models/user.model";
import { IPhoneNumber } from "@models/PhoneNumber";

const useStyles = createStyles((t) => ({
  grid: {
    gridTemplateColumns: "repeat(2, 1fr)",
    [`@media (max-width: ${t.breakpoints.xs}px)`]: {
      gridTemplateColumns: "1fr",
    },
  },
  fullWidth: {
    gridColumn: "1 / -1",
  },
}));

export const Step1 = forwardRef<
  IStepRef<IIVolunteeringFormData>,
  {
    readonly?: boolean;
    isSavePending?: boolean;
    user: IUser;
    draft?: Draft<IIVolunteeringFormData>;
    onChange?: (change: Draft<IIVolunteeringFormData>) => void;
  }
>(
  (
    {
      user,
      readonly = false,
      isSavePending = false,
      draft = {},
      onChange = () => {},
    },
    ref
  ) => {
    const setCurrentStep = useSetAtom(currentStepAtom);

    const lc = useLanguageCode();

    const { t } = useTranslation();

    const { classes } = useStyles();

    const {
      firstName,
      lastName,
      salutation,
      phone,
      email,
      dob,
      address,
      postCode,
      city,
    } = draft;
    const form = useForm({
      initialValues: {
        dob,
        city,
        address,
        postCode,
        salutation,
        phone:
          phone ||
          (!readonly
            ? user.phone
            : ({
                code: getDefaultPhoneCodeUtil(lc),
                number: "",
              } as IPhoneNumber)),
        email: email || !readonly ? user.email : "",
        lastName: lastName || !readonly ? user.lastName : "",
        firstName: firstName || !readonly ? user.firstName : "",
      },
      validate: {
        dob: (dob) => (!dob ? "Date of birth is required." : null),
      },
    });

    const handleSubmit = form.onSubmit(() => {
      setCurrentStep((pv) => pv + 1);
    });

    const $toRefs = { onChange };
    const toRefs = useRef($toRefs);
    useEffect(() => {
      toRefs.current = $toRefs;
    });

    useEffect(() => {
      toRefs.current.onChange(form.values);
    }, [form.values]);

    useImperativeHandle(ref, () => ({
      getValues: () => form.values,
      isDirty: () => form.isDirty(),
      resetDirty: () => form.resetDirty(),
    }));

    return (
      <form onSubmit={handleSubmit}>
        <SimpleGrid className={classes.grid}>
          <TextInput
            required
            label={t("First Name")}
            {...form.getInputProps("firstName")}
          />
          <TextInput
            required
            label={t("Last Name")}
            {...form.getInputProps("lastName")}
          />
          <Flex direction="column">
            <Select
              required
              label={t("Salutation")}
              data={["Herr", "Frau", "Divers"]}
              {...form.getInputProps("salutation")}
            />
            <TextInput
              required
              value={form.values.salutation || ""}
              sx={{
                height: 0,
                pointerEvents: "none",
                opacity: 0,
                "*": { height: 0, minHeight: 0 },
              }}
            />
          </Flex>
          <PhoneNumberInput
            code={form.getInputProps("phone.code")}
            number={form.getInputProps("phone.number")}
          />
          <TextInput
            required
            type="email"
            label={t("Email Address")}
            {...form.getInputProps("email")}
          />
          <DatePicker
            required
            locale={lc}
            label={t("Date of Birth")}
            {...form.getInputProps("dob")}
            value={
              form.values.dob ? dayjs(form.values.dob).toDate() : undefined
            }
            onChange={(e) =>
              form.setFieldValue("dob", e ? dayjs(e).toString() : null)
            }
          />
          <TextInput
            required
            className={classes.fullWidth}
            label={t("Address")}
            {...form.getInputProps("address")}
          />
          <TextInput
            required
            label={t("Postal code / ZIP code")}
            {...form.getInputProps("postCode")}
          />
          <TextInput
            required
            label={t("City")}
            {...form.getInputProps("city")}
          />
          {!readonly && (
            <Button
              type="submit"
              className={classes.fullWidth}
              loading={isSavePending}
            >
              {isSavePending ? `${t("Saving")}...` : t("Continue")}
            </Button>
          )}
        </SimpleGrid>
      </form>
    );
  }
);
